import React, { useState } from "react";
import clsx from "clsx";

const SimpleCheckbox = ({
  name,
  initialChecked,
  onChange,
  label,
  allowInteraction = true,
}: {
  name: string;
  initialChecked: boolean;
  onChange: (newChecked: boolean) => void;
  label?: string | React.ReactNode;
  allowInteraction?: boolean;
}) => {
  const [checkState, setCheckedState] = useState(initialChecked ?? false);

  let isLabelReactNode = React.isValidElement(label);

  return (
    <div className={clsx("flex", isLabelReactNode && "items-center", !isLabelReactNode && "gap-3")}>
      <div className="flex h-6 shrink-0 items-center">
        <div className="group grid size-4 grid-cols-1">
          <input
            name={name}
            id={name}
            type="checkbox"
            className={clsx(
              "col-start-1 row-start-1 appearance-none rounded",
              "bg-white border-[1.5px] border-primary-600",
              "checked:border-primary-600 checked:bg-primary-600",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600",
              "indeterminate:border-primary-600 indeterminate:bg-primary-600",
              "disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100",
              "forced-colors:appearance-auto",
            )}
            checked={checkState}
            onChange={(event) => {
              event.stopPropagation();
              if (!allowInteraction) {
                event.preventDefault();
                return;
              }

              const nextChecked = event.currentTarget.checked;
              if (onChange) {
                onChange(nextChecked);
              }
              setCheckedState(nextChecked);
            }}
            onClick={(event) => event.stopPropagation()}
          />
          <svg
            fill="none"
            viewBox="0 0 14 14"
            className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
          >
            <path
              d="M3 8L6 11L11 3.5"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="opacity-0 group-has-[:checked]:opacity-100"
            />
            <path
              d="M3 7H11"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="opacity-0 group-has-[:indeterminate]:opacity-100"
            />
          </svg>
        </div>
      </div>

      {label &&
        (isLabelReactNode ? (
          /* A node label still has to be a real <label>, or the input has no accessible name and
             the text stops being a click target. `contents` keeps the caller's own node as the
             flex item, so existing layout is unchanged. */
          <label htmlFor={name} className="contents">
            {label}
          </label>
        ) : (
          <div className="text-sm/6">
            <label htmlFor={name} className="font-medium text-[14px] text-neutral-900">
              {label}
            </label>
          </div>
        ))}
    </div>
  );
};

// TODO(saurabh): refactor to reduce duplication with SimpleCheckbox
function SimpleCheckboxControlled({
  name,
  checked,
  onChange,
  label,
  disabled,
  preserveCheckedAppearanceWhenDisabled = false,
  containerClassName,
}: {
  name: string;
  checked: boolean;
  onChange: (newChecked: boolean) => void;
  label?: string | React.ReactNode;
  disabled?: boolean;
  /** Keep checked boxes filled while interaction is disabled, such as during a save. */
  preserveCheckedAppearanceWhenDisabled?: boolean;
  /**
   * Replaces the default `gap-3` spacing between the input and its label. Callers that pass this
   * own the gap, so a tighter pill-style control can sit closer to its text; omit it and the
   * standard spacing applies.
   */
  containerClassName?: string;
}) {
  let isLabelReactNode = React.isValidElement(label);

  return (
    <div className={clsx("flex", containerClassName ? containerClassName : "gap-3")}>
      <div className="flex h-6 shrink-0 items-center">
        <div className="group grid size-4 grid-cols-1">
          <input
            name={name}
            id={name}
            type="checkbox"
            className={clsx(
              "col-start-1 row-start-1 appearance-none rounded",
              "bg-white border-[1.5px] border-primary-600",
              "checked:border-primary-600 checked:bg-primary-600",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600",
              "indeterminate:border-primary-600 indeterminate:bg-primary-600",
              "disabled:border-gray-300 disabled:bg-gray-100",
              preserveCheckedAppearanceWhenDisabled
                ? "disabled:checked:border-primary-600 disabled:checked:bg-primary-600"
                : "disabled:checked:bg-gray-100",
              "forced-colors:appearance-auto",
            )}
            checked={checked}
            disabled={disabled}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (disabled) {
                event.preventDefault();
                return;
              }

              event.stopPropagation();
              if (onChange) {
                onChange(event.currentTarget.checked);
              }
            }}
            onClick={(event) => event.stopPropagation()}
          />
          <svg
            fill="none"
            viewBox="0 0 14 14"
            className={clsx(
              "pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white",
              !preserveCheckedAppearanceWhenDisabled && "group-has-[:disabled]:stroke-gray-950/25",
            )}
          >
            <path
              d="M3 8L6 11L11 3.5"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="opacity-0 group-has-[:checked]:opacity-100"
            />
            <path
              d="M3 7H11"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="opacity-0 group-has-[:indeterminate]:opacity-100"
            />
          </svg>
        </div>
      </div>
      {label &&
        (isLabelReactNode ? (
          /* A node label still has to be a real <label>, or the input has no accessible name and
             the text stops being a click target. `contents` keeps the caller's own node as the
             flex item, so existing layout is unchanged. */
          <label htmlFor={name} className="contents">
            {label}
          </label>
        ) : (
          <div className="text-sm/6">
            <label htmlFor={name} className="font-medium text-[14px] text-neutral-900">
              {label}
            </label>
          </div>
        ))}
    </div>
  );
}

export { SimpleCheckbox, SimpleCheckboxControlled };
